import { useCallback } from "react";
import { buildTotalCapsulesStateUpdate } from "../utils/viewPafFormLogic";

/**
 * Encapsulates dosage-field edits (strengths, cycles, weeks) so ViewPAF stays readable.
 * Behaviour matches the legacy inline `handlePrescriptionFields` implementation.
 */
export function useViewPafPrescriptionFields({
  form,
  setForm,
  displayWeeks,
  handleAditionalOptionsChange,
}) {
  const applyTotalCapsulesFromForm = useCallback(
    (formSnapshot) => {
      const patch = buildTotalCapsulesStateUpdate(formSnapshot, displayWeeks);
      if (!patch) return;
      setForm((prev) => ({ ...prev, ...patch }));
    },
    [displayWeeks, setForm]
  );

  const handlePrescriptionFields = useCallback(
    (selectedOption, value, name) => {
      if (value === "incomplete_strengths") {
        const newStrength = {
          strength_value: parseFloat(selectedOption.label),
          strength_id: "Incomplete",
          new_strength_id: selectedOption?.id,
          capsules: "Incomplete",
        };

        const incompleteStrengths = Array.isArray(form?.strengths)
          ? [...form.strengths]
          : [];

        incompleteStrengths.push(newStrength);

        setForm({ ...form, strengths: incompleteStrengths });
      }

      if (name === "capsule_strength") {
        const formStrengths = form?.strengths;
        const new_capsuleStrength = [...formStrengths];

        const indexToUpdate = new_capsuleStrength?.findIndex(
          (strength) => strength.strength_id === value?.strength_id
        );

        const numericPart = parseFloat(selectedOption.label);

        if (indexToUpdate !== -1) {
          const updatedFormStrengths = [...formStrengths];
          if (
            updatedFormStrengths[indexToUpdate].old_strenght_Value === undefined
          ) {
            updatedFormStrengths[indexToUpdate].old_strenght_Value =
              new_capsuleStrength[indexToUpdate].strength_value;
          }
          updatedFormStrengths[indexToUpdate].strength_value = numericPart;

          new_capsuleStrength[indexToUpdate].new_strength_id = selectedOption.id;

          handleAditionalOptionsChange(
            !!selectedOption,
            "select",
            "capsuleStrength"
          );

          form?.strengths?.forEach((item) => {
            if (item.strength_id === item.new_strength_id) {
              item.new_strength_id = null;
            }
          });

          setForm({ ...form, strengths: updatedFormStrengths });
          applyTotalCapsulesFromForm({ ...form, strengths: updatedFormStrengths });
        }
      }

      if (value === "capsule_cycles") {
        const updateCapsuleCycles = { ...form };
        updateCapsuleCycles.new_capsule_cycles = selectedOption;
        updateCapsuleCycles.n_weeks_other = selectedOption?.id === 0 ? 1 : null;
        updateCapsuleCycles.n_weeks = selectedOption?.id === 0 && 0;
        updateCapsuleCycles.new_totalCapsules =
          +form?.total_capsules * parseFloat(selectedOption.label);

        const capsuleCycleValue = updateCapsuleCycles.new_capsule_cycles?.id
          ? updateCapsuleCycles.new_capsule_cycles.id
          : parseInt(form?.n_cycles?.match(/\d+/)[0], 10);

        const result =
          capsuleCycleValue *
          (updateCapsuleCycles?.new_weeks_per_cycle?.id === 0
            ? updateCapsuleCycles?.new_weeks_per_cycle_other
            : updateCapsuleCycles?.new_weeks_per_cycle?.id !== 0
              ? updateCapsuleCycles?.new_weeks_per_cycle?.id
              : updateCapsuleCycles?.weeks_per_cycle);

        updateCapsuleCycles.new_total_weeks_supply = result;

        handleAditionalOptionsChange(selectedOption.id, "select", "n_cycles");
        setForm(updateCapsuleCycles);
        applyTotalCapsulesFromForm(updateCapsuleCycles);
      }

      if (value === "number_weeks_other") {
        let selectedValue = parseInt(selectedOption, 10);
        if (isNaN(selectedValue) || selectedValue < 1) {
          selectedValue = 1;
        } else if (selectedValue > 12) {
          selectedValue = 12;
        }

        const updateCapsuleWeeks = { ...form };

        updateCapsuleWeeks.n_weeks_other = selectedValue;

        updateCapsuleWeeks.n_weeks = 0;
        updateCapsuleWeeks.new_totalCapsules =
          (+form?.capsule_strength * selectedValue) / 4;

        setForm(updateCapsuleWeeks);
        handleAditionalOptionsChange(selectedValue.id, "select", "n_weeks");
        applyTotalCapsulesFromForm(updateCapsuleWeeks);
      }

      if (value === "weeks_per_cycle") {
        const updateWeeksPerCycle = { ...form };
        updateWeeksPerCycle.new_weeks_per_cycle = selectedOption;
        updateWeeksPerCycle.new_weeks_per_cycle_other =
          selectedOption.id === 0 ? 1 : null;

        const cycles =
          updateWeeksPerCycle.new_capsule_cycles?.id ||
          parseInt(form?.n_cycles?.match(/\d+/)[0], 10);

        updateWeeksPerCycle.new_totalCapsules =
          cycles * parseFloat(selectedOption.label);

        const capsuleCycleValue = updateWeeksPerCycle.new_capsule_cycles?.id
          ? updateWeeksPerCycle.new_capsule_cycles.id
          : parseInt(form?.n_cycles?.match(/\d+/)[0], 10);

        const result =
          capsuleCycleValue *
          (updateWeeksPerCycle?.new_weeks_per_cycle?.id === 0
            ? updateWeeksPerCycle?.new_weeks_per_cycle_other
            : updateWeeksPerCycle?.new_weeks_per_cycle?.id !== 0
              ? updateWeeksPerCycle?.new_weeks_per_cycle?.id
              : updateWeeksPerCycle?.weeks_per_cycle);

        updateWeeksPerCycle.new_total_weeks_supply = result;

        setForm(updateWeeksPerCycle);
        applyTotalCapsulesFromForm(updateWeeksPerCycle);
        handleAditionalOptionsChange(
          selectedOption.id,
          "select",
          "weeks_per_cycle"
        );
      }

      if (value === "weeks_per_cycle_other") {
        let selectedValue = parseInt(selectedOption, 10);
        if (isNaN(selectedValue) || selectedValue < 1) {
          selectedValue = 1;
        } else if (selectedValue > 12) {
          selectedValue = 12;
        }

        const updateTotalWeeksPerCycle = { ...form };

        updateTotalWeeksPerCycle.new_weeks_per_cycle_other = selectedValue;

        const capsuleCycleValue = updateTotalWeeksPerCycle.new_capsule_cycles?.id
          ? updateTotalWeeksPerCycle.new_capsule_cycles.id
          : parseInt(form?.n_cycles?.match(/\d+/)[0], 10);

        const result =
          capsuleCycleValue *
          (updateTotalWeeksPerCycle?.new_weeks_per_cycle?.id === 0
            ? updateTotalWeeksPerCycle?.new_weeks_per_cycle_other
            : updateTotalWeeksPerCycle?.new_weeks_per_cycle?.id !== 0
              ? updateTotalWeeksPerCycle?.new_weeks_per_cycle?.id
              : updateTotalWeeksPerCycle?.weeks_per_cycle);

        updateTotalWeeksPerCycle.new_total_weeks_supply = result;

        setForm(updateTotalWeeksPerCycle);
        handleAditionalOptionsChange(selectedValue.id, "select", "n_weeks");
        applyTotalCapsulesFromForm(updateTotalWeeksPerCycle);
      }

      if (name === "capsules" || value === "capsules") {
        const matchingObject = form?.strengths?.find(
          (obj) => obj.strength_id === value?.strength_id
        );

        let nextSelected = selectedOption;
        const parsedValue = parseInt(nextSelected);
        nextSelected = parsedValue ? parsedValue.toString() : nextSelected;
        if (+nextSelected <= 0) {
          nextSelected = 1;
        } else if (+nextSelected > 200) {
          nextSelected = "200";
        }

        if (name >= 0 && name < form?.strengths?.length) {
          form.strengths[name].new_capsules_other = parseFloat(nextSelected);
          form.strengths[name].is_new_other_capsules = true;
        }

        if (matchingObject) {
          matchingObject.new_capsules =
            nextSelected.id === 0
              ? nextSelected.label
              : parseFloat(nextSelected.label);
          matchingObject.new_capsules_other = null;
          matchingObject.is_new_other_capsules = false;
        }
        handleAditionalOptionsChange(nextSelected, "select", "capsulePerCycle");
        if (
          !!matchingObject &&
          matchingObject?.new_capsules === "Other (please state)" &&
          matchingObject?.new_capsules_other === null
        ) {
          matchingObject.new_capsules_other = 1;
          matchingObject.is_new_other_capsules = true;
        }

        setForm({ ...form });
        applyTotalCapsulesFromForm({ ...form });
      }
    },
    [applyTotalCapsulesFromForm, form, handleAditionalOptionsChange, setForm]
  );

  return { handlePrescriptionFields };
}
